import { useState, useRef, useEffect, useCallback } from "react";
import { MapPin, Heart, AlertTriangle, Search, Dog, ClipboardEdit, BadgeCheck, PlusCircle, ArrowRight, ChevronDown, X } from "lucide-react";
import type { Shelter } from "@/types/shelter";
import { isUrgentNeedsActive } from "@/types/shelter";
import heroBg from "@/assets/hero-bg.png";
import { Button } from "@/components/ui/button";
import { ShelterSearchDialog } from "@/components/ShelterSearchDialog";
import { useCountUp } from "@/hooks/useCountUp";

interface HeroSectionProps {
  shelters: Shelter[];
  onScrollToMap: () => void;
  onShelterSelect: (shelter: Shelter) => void;
}

const pluralCities = (n: number): string => {
  if (n === 1) return "miasto";
  const lastTwo = n % 100;
  const lastOne = n % 10;
  if (lastTwo >= 12 && lastTwo <= 14) return "miast";
  if (lastOne >= 2 && lastOne <= 4) return "miasta";
  return "miast";
};

const pluralShelters = (n: number): string => {
  if (n === 1) return "schronisko";
  const lastTwo = n % 100;
  const lastOne = n % 10;
  if (lastTwo >= 12 && lastTwo <= 14) return "schronisk";
  if (lastOne >= 2 && lastOne <= 4) return "schroniska";
  return "schronisk";
};

const pluralUrgent = (n: number): string => {
  if (n === 1) return "pilna potrzeba";
  const lastTwo = n % 100;
  const lastOne = n % 10;
  if (lastTwo >= 12 && lastTwo <= 14) return "pilnych potrzeb";
  if (lastOne >= 2 && lastOne <= 4) return "pilne potrzeby";
  return "pilnych potrzeb";
};

export const HeroSection = ({ shelters, onScrollToMap, onShelterSelect }: HeroSectionProps) => {
  const [searchOpen, setSearchOpen] = useState(false);
  const [hidden, setHidden] = useState(() => localStorage.getItem("hero-hidden") === "true");
  const [animating, setAnimating] = useState(false);
  const heroRef = useRef<HTMLDivElement>(null);

  const stats = {
    total: shelters.length,
    urgent: shelters.filter(isUrgentNeedsActive).length,
    verified: shelters.filter(s => s.organization_verified).length,
    cities: new Set(shelters.map(s => s.city)).size,
  };

  const animTotal = useCountUp(stats.total);
  const animUrgent = useCountUp(stats.urgent);
  const animVerified = useCountUp(stats.verified);
  const animCities = useCountUp(stats.cities);

  const handleHide = useCallback(() => {
    setAnimating(true);
    localStorage.setItem("hero-hidden", "true");
    // Wait for animation to finish before fully hiding
    setTimeout(() => {
      setHidden(true);
      setAnimating(false);
    }, 400);
  }, []);

  const handleShow = useCallback(() => {
    setHidden(false);
    localStorage.removeItem("hero-hidden");
  }, []);

  if (hidden && !animating) {
    return (
      <div className="border-b border-border bg-accent/30 py-1.5 px-3 flex items-center justify-center gap-2">
        <button
          onClick={handleShow}
          className="flex items-center gap-1.5 text-xs font-medium text-primary hover:text-primary/80 transition-colors"
        >
          <ChevronDown className="w-3.5 h-3.5" />
          Pokaż sekcję powitalną
        </button>
      </div>
    );
  }

  return (
    <div
      ref={heroRef}
      className="overflow-hidden transition-all duration-400 ease-in-out"
      style={{
        maxHeight: animating ? '0px' : '100vh',
        opacity: animating ? 0 : 1,
      }}
    >
      <section className="border-b border-border overflow-hidden relative min-h-[calc(100dvh-56px)] sm:min-h-0 flex flex-col">
        <img 
          src={heroBg} 
          alt="" 
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-background/20" />

        <div className="relative max-w-screen-lg mx-auto px-5 pt-3 pb-4 sm:px-4 sm:py-10 flex-1 flex flex-col">
          {/* Hide button */}
          <button
            onClick={handleHide}
            className="absolute top-2 right-2 sm:top-4 sm:right-4 flex items-center gap-1 text-[10px] sm:text-xs font-medium text-muted-foreground/60 hover:text-muted-foreground transition-colors z-10"
          >
            <X className="w-3 h-3 sm:w-3.5 sm:h-3.5" />
            <span className="hidden sm:inline">Ukryj</span>
          </button>

          <div className="text-center mb-2 sm:mb-6 mt-5 sm:mt-0 animate-fade-in">
            <h1 className="text-lg sm:text-3xl md:text-4xl font-extrabold text-primary mb-1 sm:mb-2 leading-tight">
              Pomaganie zwierzakom zaczyna się tutaj 💚
            </h1>
            <p className="text-muted-foreground text-xs sm:text-base max-w-lg mx-auto">
              Społecznościowa mapa schronisk z aktualnymi potrzebami, tworzona przez wolontariuszy i schroniska
            </p>
          </div>

          {shelters.length > 0 && (
            <div className="flex flex-wrap justify-center gap-1.5 sm:gap-3 mb-2 sm:mb-6 animate-fade-in [animation-delay:100ms] [animation-fill-mode:backwards]">
              <div className="flex items-center gap-1 sm:gap-1.5 bg-primary/10 text-primary px-2.5 py-1 sm:px-3.5 sm:py-2 rounded-full">
                <MapPin className="w-3 h-3 sm:w-3.5 sm:h-3.5" />
                <span className="font-extrabold text-xs sm:text-sm tabular-nums">{animTotal}</span>
                <span className="font-semibold text-[10px] sm:text-xs opacity-80">{pluralShelters(animTotal)}</span>
              </div>
              {stats.verified > 0 && (
                <div className="hidden sm:flex items-center gap-1 sm:gap-1.5 bg-primary/10 text-primary px-2.5 py-1 sm:px-3.5 sm:py-2 rounded-full">
                  <BadgeCheck className="w-3 h-3 sm:w-3.5 sm:h-3.5" />
                  <span className="font-extrabold text-xs sm:text-sm tabular-nums">{animVerified}</span>
                  <span className="font-semibold text-[10px] sm:text-xs opacity-80">zweryfikowanych</span>
                </div>
              )}
              {stats.urgent > 0 && (
                <div className="flex items-center gap-1 sm:gap-1.5 bg-urgent/10 text-urgent px-2.5 py-1 sm:px-3.5 sm:py-2 rounded-full">
                  <AlertTriangle className="w-3 h-3 sm:w-3.5 sm:h-3.5" />
                  <span className="font-extrabold text-xs sm:text-sm tabular-nums">{animUrgent}</span>
                  <span className="font-semibold text-[10px] sm:text-xs opacity-80">{pluralUrgent(animUrgent)}</span>
                </div>
              )}
              <div className="flex items-center gap-1 sm:gap-1.5 bg-primary/10 text-primary px-2.5 py-1 sm:px-3.5 sm:py-2 rounded-full">
                <MapPin className="w-3 h-3 sm:w-3.5 sm:h-3.5" />
                <span className="font-extrabold text-xs sm:text-sm tabular-nums">{animCities}</span>
                <span className="font-semibold text-[10px] sm:text-xs opacity-80">{pluralCities(animCities)}</span>
              </div>
            </div>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-6 max-w-4xl mx-auto w-full flex-1 content-center">
            <div className="flex flex-col p-4 sm:p-7 rounded-2xl bg-card/55 backdrop-blur-md shadow-sm border border-white/30 animate-fade-in [animation-delay:200ms] [animation-fill-mode:backwards]">
              <div className="flex items-center gap-2 mb-2 sm:mb-4">
                <Heart className="w-5 h-5 sm:w-5 sm:h-5 text-primary" />
                <span className="font-extrabold text-[15px] sm:text-lg text-foreground">Chcę pomóc</span>
              </div>
              <ul className="space-y-1.5 sm:space-y-3 flex-1">
                <li className="flex items-start gap-2 text-[13px] sm:text-sm text-muted-foreground">
                  <Search className="w-3.5 h-3.5 sm:w-3.5 sm:h-3.5 text-primary mt-0.5 flex-shrink-0" />
                  <span className="font-medium">Wyszukaj schronisko w Twojej okolicy</span>
                </li>
                <li className="flex items-start gap-2 text-[13px] sm:text-sm text-muted-foreground">
                  <AlertTriangle className="w-3.5 h-3.5 sm:w-3.5 sm:h-3.5 text-primary mt-0.5 flex-shrink-0" />
                  <span className="font-medium">Sprawdź aktualne potrzeby, np. karma, koce</span>
                </li>
                <li className="flex items-start gap-2 text-[13px] sm:text-sm text-muted-foreground">
                  <Dog className="w-3.5 h-3.5 sm:w-3.5 sm:h-3.5 text-primary mt-0.5 flex-shrink-0" />
                  <span className="font-medium">Idź na spacer z psem lub zostań wolontariuszem</span>
                </li>
              </ul>
              <Button variant="outline" className="w-full mt-3 sm:mt-5 h-9 sm:h-10 text-[13px] sm:text-sm border-primary/30 text-primary hover:bg-primary/10 hover:text-primary" size="sm" onClick={onScrollToMap}>
                <ArrowRight className="w-4 h-4" />
                Zobacz, gdzie możesz pomóc
              </Button>
            </div>

            <div className="flex flex-col p-4 sm:p-7 rounded-2xl bg-card/55 backdrop-blur-md shadow-sm border border-white/30 animate-fade-in [animation-delay:350ms] [animation-fill-mode:backwards]">
              <div className="flex items-center gap-2 mb-2 sm:mb-4">
                <ClipboardEdit className="w-5 h-5 sm:w-5 sm:h-5 text-primary" />
                <span className="font-extrabold text-[15px] sm:text-lg text-foreground">Reprezentuję schronisko</span>
              </div>
              <ul className="space-y-1.5 sm:space-y-3 flex-1">
                <li className="flex items-start gap-2 text-[13px] sm:text-sm text-muted-foreground">
                  <PlusCircle className="w-3.5 h-3.5 sm:w-3.5 sm:h-3.5 text-primary mt-0.5 flex-shrink-0" />
                  <span className="font-medium">Dodaj schronisko do bazy</span>
                </li>
                <li className="flex items-start gap-2 text-[13px] sm:text-sm text-muted-foreground">
                  <ClipboardEdit className="w-3.5 h-3.5 sm:w-3.5 sm:h-3.5 text-primary mt-0.5 flex-shrink-0" />
                  <span className="font-medium">Zaktualizuj potrzeby i dane kontaktowe</span>
                </li>
                <li className="flex items-start gap-2 text-[13px] sm:text-sm text-muted-foreground">
                  <BadgeCheck className="w-3.5 h-3.5 sm:w-3.5 sm:h-3.5 text-primary mt-0.5 flex-shrink-0" />
                  <span className="font-medium">Zweryfikuj organizację i zyskaj odznakę</span>
                </li>
              </ul>
              <Button variant="outline" className="w-full mt-3 sm:mt-5 h-9 sm:h-10 text-[13px] sm:text-sm border-primary/30 text-primary hover:bg-primary/10 hover:text-primary" size="sm" onClick={() => setSearchOpen(true)}>
                <ArrowRight className="w-4 h-4" />
                Zaktualizuj lub dodaj schronisko
              </Button>
            </div>
          </div>

          <button 
            onClick={onScrollToMap}
            className="flex flex-col items-center gap-0.5 mx-auto mt-auto pt-3 sm:mt-5 text-primary/60 hover:text-primary active:text-primary transition-colors"
          >
            <span className="text-[10px] sm:text-xs font-medium">Przejdź do mapy</span>
            <ChevronDown className="w-4 h-4 sm:w-5 sm:h-5 animate-bounce" />
          </button>
        </div>

        <ShelterSearchDialog open={searchOpen} onOpenChange={setSearchOpen} shelters={shelters} onShelterSelect={onShelterSelect} />
      </section>
    </div>
  );
};
